import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuth } from '../components/AuthContext';
import HeaderLink from '../components/ui/HeaderLink';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { Typography, Box } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import XMarkIcon from '@heroicons/react/20/solid/XMarkIcon';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocalMallIcon from '@mui/icons-material/LocalMall';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';

import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useCategory } from './CategoryContext';
import { useBasket } from './BasketContext';
import { navigationLinks } from './ui/data/navigationLinks';
import api from '../pages/api';
import { CSSTransition } from 'react-transition-group';
import { useMenu } from './MenuContext';

export default function Header() {
    const { isAuthorized } = useAuth();
    const [searchQuery, setSearchQuery] = useState("");
    const [showMobilePanel, setShowMobilePanel] = useState(false);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
    const [recentSearches, setRecentSearches] = useState([]);
    const [trendingItems, setTrendingItems] = useState([]);
    const searchRef = useRef(null);
    const dropdownRef = useRef(null);
    const mobileSearchRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { basketCount, isUpdating } = useBasket();
    const [isSearchMode, setIsSearchMode] = useState(false);
    const { menuItems, isLoading } = useMenu();
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
    const profileDropdownRef = useRef(null);
    const [isSessionFetching, setIsSessionFetching] = useState(false);

    const currentPath = location.pathname.split('/')[1]; // Get the first slug in the URL

    const toggleMobilePanel = () => {
        setShowMobilePanel(!showMobilePanel);
    };

    const handleSearchFocus = () => {
        setIsSearchFocused(true);
        setIsLanguageDropdownOpen(false); // Close language dropdown when search is focused
    };

    const handleSearchBlur = () => {
        setTimeout(() => {
            setIsSearchFocused(false);
        }, 200);
    };

    const handleRemoveRecentSearch = async (termToRemove) => {
        try {
            await api.delete(`/api/search/recent/?term=${encodeURIComponent(termToRemove)}`);
            setRecentSearches(prev => prev.filter(term => term !== termToRemove));
        } catch (error) {
            console.error('Error removing recent search:', error);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                isSearchFocused && 
                !searchRef.current?.contains(event.target) && 
                !mobileSearchRef.current?.contains(event.target) && 
                !dropdownRef.current?.contains(event.target)
            ) {
                setIsSearchFocused(false);
            }
        };

        const handleResize = () => {
            if (dropdownRef.current && searchRef.current) {
                const searchRect = searchRef.current.getBoundingClientRect();
                dropdownRef.current.style.top = `${searchRect.bottom + window.scrollY}px`;
                dropdownRef.current.style.left = `${searchRect.left + window.scrollX}px`;
                dropdownRef.current.style.width = `${searchRect.width}px`;
            }
        };

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                if (document.activeElement === searchRef.current?.querySelector('input')) {
                    searchRef.current?.querySelector('input')?.blur();
                    setIsSearchFocused(false);
                    setIsSearchMode(false);
                    setSearchQuery('');
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('resize', handleResize);
        document.addEventListener('keydown', handleEscapeKey);
        handleResize(); // Initial call to set position

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('resize', handleResize);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, []);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const query = searchParams.get('q') || '';
        setSearchQuery(query);
    }, [location.search]);

    const trackSearch = useCallback(async (searchTerm) => {
        try {
            await api.post('/api/search/track/', {
                word: searchTerm
            });
        } catch (error) {
            console.error('Error tracking search:', error);
            // Continue with search even if tracking fails
        }
    }, []);

    const handleSearch = async (e) => {
        e.preventDefault();
        const trimmedQuery = searchQuery.trim();
        
        if (trimmedQuery) {
            try {
                // Track the search first
                await trackSearch(trimmedQuery);
                
                // Add to recent searches if not already present
                if (!recentSearches.includes(trimmedQuery)) {
                    setRecentSearches(prev => [trimmedQuery, ...prev].slice(0, 5));
                }
            } catch (error) {
                console.error('Error in search handling:', error);
            } finally {
                // Always navigate to search results, even if tracking fails
                navigate(`/search?q=${encodeURIComponent(trimmedQuery)}`);
                setIsSearchFocused(false);
            }
        }
    };

    const handleSearchInputChange = (e) => {
        setSearchQuery(e.target.value);
    };

    // Add new scroll function
    const scrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: 'instant' // This ensures immediate scroll without animation
        });
    };

    // Add this helper function to handle all three actions
    const handleMobileLinkClick = (url) => {
        // Scroll to top instantly
        window.scrollTo({
            top: 0,
            behavior: 'instant'
        });
        // Close the mobile panel
        setShowMobilePanel(false);
        // Navigate to the page
        navigate(url);
    };

    // Add this helper function to check if a path is active
    const isActivePath = (path) => {
        return location.pathname.startsWith(path);
    };

    // Helper function to build search URLs
    const buildSearchUrl = (searchTerm) => `/search?q=${encodeURIComponent(searchTerm)}`;

    // Fetch recent searches
    useEffect(() => {
        const fetchRecentSearches = async () => {
            try {
                const response = await api.get('/api/search/recent/');
                setRecentSearches(response.data);
            } catch (error) {
                console.error('Error fetching recent searches:', error);
            }
        };

        fetchRecentSearches();
    }, []);

    // Fetch trending searches
    useEffect(() => {
        let isMounted = true;
        const fetchTrendingSearches = async () => {
            try {
                const response = await api.get('/api/search/trending/');
                if (isMounted) {
                    setTrendingItems(response.data.map(item => item.word));
                }
            } catch (error) {
                console.error('Error fetching trending searches:', error);
            }
        };

        if (!isSessionFetching) {
            fetchTrendingSearches();
        }

        const refreshInterval = setInterval(() => {
            if (!isSessionFetching) {
                fetchTrendingSearches();
            }
        }, 5 * 60 * 1000); // Refresh every 5 minutes
        
        return () => {
            isMounted = false;
            clearInterval(refreshInterval);
        };
    }, [isSessionFetching]);

    // Update the SearchDropdown component
    const SearchDropdown = () => {
        if (!isSearchFocused) return null;

        return (
            <div 
                ref={dropdownRef}
                className="fixed lg:absolute bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden w-[calc(100%-20px)] lg:w-full mx-[10px] lg:mx-0"
                style={{
                    top: window.innerWidth >= 1024 ? 'calc(100% + 4px)' : '120px', // Changed from 140px to 120px
                    left: window.innerWidth >= 1024 ? 0 : undefined,
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    zIndex: 1000
                }}
            >
                {recentSearches.length > 0 && (
                    <div className="p-3 border-b border-gray-100">
                        <h3 className="text-xs font-semibold text-gray-500 mb-2">Recent Searches</h3>
                        <ul className="space-y-2">
                            {recentSearches.map((item, index) => (
                                <li key={index} className="flex items-center justify-between">
                                    <Link 
                                        to={buildSearchUrl(item)}
                                        className="text-sm text-gray-700 hover:text-blue-600"
                                        onClick={() => setIsSearchFocused(false)}
                                        aria-label={`Search for ${item}`}
                                    >
                                        {item}
                                    </Link>
                                    <button
                                        onClick={() => handleRemoveRecentSearch(item)}
                                        className="p-1 text-gray-400 hover:text-gray-600"
                                        aria-label={`Remove ${item} from recent searches`}
                                    >
                                        <XMarkIcon className="h-4 w-4" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
                
                {trendingItems.length > 0 && (
                    <div className="p-3">
                        <h3 className="text-xs font-semibold text-gray-500 mb-2">Trending Searches</h3>
                        <ul className="space-y-2">
                            {trendingItems.map((item, index) => (
                                <li key={index}>
                                    <Link 
                                        to={buildSearchUrl(item)}
                                        className="text-sm text-gray-700 hover:text-blue-600 text-left block w-full"
                                        onClick={() => setIsSearchFocused(false)}
                                        aria-label={`Search for ${item}`}
                                    >
                                        {item}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        );
    };

    // Update the useEffect for positioning to handle mobile view
    useEffect(() => {
        const handleResize = () => {
            if (!dropdownRef.current) return;
            
            if (window.innerWidth >= 1024) {
                // Desktop positioning
                const searchRect = searchRef.current?.getBoundingClientRect();
                if (searchRect) {
                    dropdownRef.current.style.top = `${searchRect.bottom + window.scrollY}px`;
                    dropdownRef.current.style.left = `${searchRect.left}px`;
                    dropdownRef.current.style.width = `${searchRect.width}px`;
                }
            } else {
                // Mobile positioning
                const mobileSearchRect = mobileSearchRef.current?.getBoundingClientRect();
                if (mobileSearchRect) {
                    dropdownRef.current.style.width = `calc(100% - 20px)`; // 10px padding on each side
                    dropdownRef.current.style.left = '10px';
                    dropdownRef.current.style.right = '10px';
                }
            }
        };

        // Add event listeners
        window.addEventListener('resize', handleResize);
        window.visualViewport?.addEventListener('resize', handleResize);
        window.visualViewport?.addEventListener('scroll', handleResize);

        // Initial position
        handleResize();

        return () => {
            window.removeEventListener('resize', handleResize);
            window.visualViewport?.removeEventListener('resize', handleResize);
            window.visualViewport?.removeEventListener('scroll', handleResize);
        };
    }, [isSearchFocused]);

    const toggleSearchMode = () => {
        setIsSearchMode(!isSearchMode);
        if (!isSearchMode) {
            // When opening search, focus the input
            setTimeout(() => {
                searchRef.current?.querySelector('input')?.focus();
            }, 100);
        }
    };

    const handleSearchCancel = () => {
        setIsSearchMode(false);
        setSearchQuery('');
        setIsSearchFocused(false);
    };

    // Add these styles to your CSS file or add them inline in the component
    const styles = {
        enter: 'transition-all duration-300 ease-out',
        enterFrom: 'opacity-0 transform -translate-x-full',
        enterTo: 'opacity-100 transform translate-x-0',
        leave: 'transition-all duration-300 ease-in',
        leaveFrom: 'opacity-100 transform translate-x-0',
        leaveTo: 'opacity-0 transform translate-x-full'
    };

    const { selectCategoryFromMenu } = useCategory();

    const handleMenuItemClick = (item) => {
        console.group('Header: handleMenuItemClick');
        console.log('Item:', item);

        // Scroll to top first
        scrollToTop();

        // Handle "Shop All" case first
        if (item.name === "SHOP ALL" || item.url === '/') {
            console.log('Shop All clicked - setting root category');
            // First set the category to null
            selectCategoryFromMenu({
                category: {
                    id: null,
                    parent_id: null,
                    title: 'All Products'
                }
            });
            
            // Then navigate to root with reset state
            navigate('/', { 
                replace: true,
                state: { 
                    resetCatalog: true
                }
            });
            
            setActiveDropdown(null);
            if (showMobilePanel) {
                toggleMobilePanel();
            }
            console.groupEnd();
            return;
        }

        // Handle dropdown behavior
        if (item.item_type === 'dropdown') {
            setActiveDropdown(activeDropdown === item.id ? null : item.id);
        }

        // Handle category navigation (for both regular and dropdown items)
        if (item.category) {
            console.log('Navigating to category:', item.category);
            selectCategoryFromMenu(item);
            navigate('/');
        }
        // Handle other URL navigation
        else if (item.url) {
            console.log('Navigating to URL:', item.url);
            navigate(item.url);
        }

        // Close mobile panel if open
        if (showMobilePanel) {
            toggleMobilePanel();
        }

        console.groupEnd();
    };

    const handleSubItemClick = (subItem, parentItem) => {
        console.group('Header: handleSubItemClick');
        console.log('SubItem:', subItem);
        console.log('Parent:', parentItem);

        // Scroll to top first
        scrollToTop();

        // Handle "Shop All" case for subitems
        if (subItem.name === "SHOP ALL" || subItem.url === '/') {
            console.log('Shop All clicked - setting root category');
            // First set the category to null
            selectCategoryFromMenu({
                category: {
                    id: null,
                    parent_id: null,
                    title: 'All Products'
                }
            });
            
            // Then navigate to root with reset state
            navigate('/', { 
                replace: true,
                state: { 
                    resetCatalog: true
                }
            });
            
            setActiveDropdown(null);
            if (showMobilePanel) {
                toggleMobilePanel();
            }
            console.groupEnd();
            return;
        }

        // Handle category navigation
        if (subItem.category) {
            console.log('Navigating to category:', subItem.category);
            selectCategoryFromMenu(subItem, parentItem);
            navigate('/');
        }
        // Handle URL navigation
        else if (subItem.url) {
            console.log('Navigating to URL:', subItem.url);
            navigate(subItem.url);
        }

        // Close mobile panel and dropdown
        if (showMobilePanel) {
            toggleMobilePanel();
        }
        setActiveDropdown(null);

        console.groupEnd();
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
                setIsProfileDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <>
            <header className="fixed top-0 left-0 right-0 flex flex-wrap md:flex-col md:justify-start md:flex-nowrap z-[50] w-full bg-white border-b border-gray-200 dark:bg-neutral-800 dark:border-neutral-700">
                {/* Topbar */}
                <div className="w-full bg-[#1e73be] hidden md:block">
                    <div className="max-w-7xl mx-auto px-[10px]">
                        <div className="flex items-center justify-between pt-2 pb-2">
                            {/* Flag and Contact Section - Left Side */}
                            <div className="flex items-center gap-4">
                                <img 
                                    src="/img/flags/icons/za.png" 
                                    alt="South African Flag" 
                                    className="h-3 w-auto"
                                />
                                <div className="flex items-center text-white text-sm gap-x-2">
                                    <Link to="/contact" className="font-semibold hover:text-gray-200 hover:underline">Contact Us</Link>
                                    <span>|</span>
                                    <a href="tel:+27825626644" className="hover:text-gray-200 flex items-center">
                                        <PhoneIcon sx={{ fontSize: '14px' }} className="mr-1" />
                                        +27(0) 82 562 6644
                                    </a>
                                    <span>|</span>
                                    <a href="mailto:info@relightsa.co.za" className="hover:text-gray-200">info@relightsa.co.za</a>
                                </div>
                            </div>

                            {/* Right Side - Account Section */}
                            <div className="flex-1 flex items-center justify-end">
                                <div className="bg-blue-500/30 text-white px-4 py-2 rounded-lg mr-6 font-bold text-sm">
                                    FREE DELIVERY ON ORDERS OVER R1500
                                </div>

                                {isAuthorized ? (
                                    <div className="relative" ref={profileDropdownRef}>
                                        <button
                                            onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                                            className="py-2 px-2.5 inline-flex items-center gap-x-2 font-medium text-[12px] rounded-lg bg-[#FDB813] text-white hover:bg-[#e5a611] focus:outline-none focus:bg-[#e5a611] disabled:opacity-50 disabled:pointer-events-none"
                                        >
                                            <AccountCircleIcon className="w-4 h-4" />
                                            My Account
                                            <KeyboardArrowDownIcon className={`w-4 h-4 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`} />
                                        </button>

                                        {/* Dropdown Menu */}
                                        {isProfileDropdownOpen && (
                                            <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                                                <Link
                                                    to="/account/profile"
                                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    onClick={() => setIsProfileDropdownOpen(false)}
                                                >
                                                    <AccountCircleIcon className="w-4 h-4 mr-2" />
                                                    My Profile
                                                </Link>
                                                <Link
                                                    to="/account/change-email"
                                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    onClick={() => setIsProfileDropdownOpen(false)}
                                                >
                                                    <EmailIcon className="w-4 h-4 mr-2" />
                                                    Update Email
                                                </Link>
                                                <Link
                                                    to="/account/change-password"
                                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    onClick={() => setIsProfileDropdownOpen(false)}
                                                >
                                                    <LockIcon className="w-4 h-4 mr-2" />
                                                    Change Password
                                                </Link>
                                                <Link
                                                    to="/shop/orders"
                                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    onClick={() => setIsProfileDropdownOpen(false)}
                                                >
                                                    <LocalShippingIcon className="w-4 h-4 mr-2" />
                                                    My Orders
                                                </Link>
                                                <div className="border-t border-gray-200 my-1"></div>
                                                <Link
                                                    to="/logout"
                                                    className="flex items-center px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                                                    onClick={() => setIsProfileDropdownOpen(false)}
                                                >
                                                    <LogoutIcon className="w-4 h-4 mr-2" />
                                                    Logout
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex flex-wrap items-center">
                                        <Link
                                            className="py-2 px-2.5 inline-flex items-center gap-x-2 font-medium text-[12px] rounded-lg text-white hover:text-white bg-blue-600/80 hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none dark:focus:ring-offset-neutral-800"
                                            to="/login"
                                        >
                                            <LockIcon className="w-4 h-4" />
                                            Login
                                        </Link>
                                        <Link
                                            className="py-2 px-2.5 inline-flex items-center gap-x-2 font-medium text-[12px] rounded-lg bg-[#FDB813] text-white hover:bg-[#e5a611] focus:outline-none focus:bg-[#e5a611] disabled:opacity-50 disabled:pointer-events-none ml-1.5"
                                            to="/register"
                                        >
                                            <AccountCircleIcon className="w-4 h-4" />
                                            Register
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                {/* End Topbar */}

                <nav className="relative w-full mx-auto text-center">
                    <div className="max-w-7xl mx-auto pl-[10px]">
                        <div className="md:flex md:items-center md:justify-between md:gap-3 py-4">
                            {/* Logo - Always visible */}
                            <div className="flex justify-between items-center gap-x-1">
                                {/* Desktop Logo */}
                                <Link
                                    className="flex-none font-semibold text-xl text-black focus:outline-none focus:opacity-80 dark:text-white hidden md:inline-block lg:inline-block"
                                    to="/"                            
                                >
                                    <img 
                                        src='/img/logo.svg' 
                                        title="RelightSA" 
                                        className="w-[240px] h-auto"
                                    />
                                </Link>

                                {/* Mobile Logo and Menu Button */}
                                <div className="flex items-center justify-between w-full md:hidden">
                                    <Link to="/" className="flex-none ml-4">
                                        <img 
                                            src='/img/logo.svg' 
                                            title="RelightSA" 
                                            className="w-[180px] h-auto"
                                        />
                                    </Link>
                                    <button
                                        onClick={() => setShowMobilePanel(!showMobilePanel)}
                                        className="lg:hidden p-2.5 ml-4 mr-4 rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 bg-white"
                                        aria-label="Toggle menu"
                                    >
                                        <MenuIcon className="h-6 w-6 text-gray-700" />
                                    </button>
                                </div>
                            </div>

                            {/* Mobile Search Bar */}
                            <div className="mt-4 md:hidden px-4">
                                <form onSubmit={handleSearch} className="w-full" ref={mobileSearchRef}>
                                    <div className="relative flex items-center">
                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                            <SearchIcon className="h-5 w-5" />
                                        </div>
                                        <input 
                                            className="w-full h-[40px] border border-gray-200 rounded-lg pl-10 pr-3 text-[15px]
                                                   focus:outline-none focus:border-gray-300 focus:ring-0
                                                   placeholder-gray-400"
                                            placeholder="Search..."
                                            value={searchQuery}
                                            onChange={handleSearchInputChange}
                                            onFocus={handleSearchFocus}
                                            onBlur={handleSearchBlur}
                                            id="mobile-search"
                                            name="mobile-search"
                                            aria-label="Search products"
                                        />
                                    </div>
                                </form>
                            </div>

                            {/* Main Navigation and Search Container */}
                            <div className="flex-1 h-[48px] hidden md:block">
                                <div id="hs-header-base" className="hs-collapse overflow-visible transition-all duration-300 basis-full grow md:block h-full">
                                    <div className="flex justify-end h-full">
                                        <div className="hidden lg:flex items-center justify-end gap-x-4 h-full pr-2 w-full">
                                            {!isSearchMode && (
                                                <nav className="flex items-center gap-x-6 h-full">
                                                    {!isLoading && menuItems.map((item) => (
                                                        <div
                                                            key={item.id}
                                                            className="relative group h-full flex items-center overflow-visible"
                                                            onMouseEnter={() => item.item_type === 'dropdown' && setActiveDropdown(item.id)}
                                                            onMouseLeave={() => setActiveDropdown(null)}
                                                        >
                                                            <div
                                                                onClick={() => handleMenuItemClick(item)}
                                                                className="text-gray-700 hover:text-blue-600 text-[15px] font-medium dark:text-neutral-200 flex items-center gap-1 h-full px-2 cursor-pointer"
                                                            >
                                                                {item.name}
                                                                {item.item_type === 'dropdown' && (
                                                                    <KeyboardArrowDownIcon className="w-4 h-4" />
                                                                )}
                                                            </div>

                                                            {item.item_type === 'dropdown' && activeDropdown === item.id && (
                                                                <div
                                                                    className="absolute left-0 top-full bg-white border border-gray-200 rounded-md shadow-lg z-[60] min-w-[200px] overflow-visible py-2"
                                                                    style={{ marginTop: '1px' }}
                                                                >
                                                                    {item.subitems?.map((subitem) => (
                                                                        <div
                                                                            key={subitem.id}
                                                                            onClick={() => handleSubItemClick(subitem, item)}
                                                                            className="block w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                                                                        >
                                                                            {subitem.name}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </nav>
                                            )}

                                            {isSearchMode && (
                                                <form onSubmit={handleSearch} className="h-full flex-1 min-w-0 relative" ref={searchRef}>
                                                    <div className="relative flex items-center h-full">
                                                        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                                            <SearchIcon className="h-5 w-5" />
                                                        </div>
                                                        <input
                                                            className="w-full h-[48px] border border-gray-200 rounded-lg pl-10 pr-16 text-[15px] text-left
                                                               focus:outline-none focus:border-gray-300 focus:ring-0
                                                               placeholder-gray-400"
                                                            placeholder="Search..."
                                                            value={searchQuery}
                                                            onChange={handleSearchInputChange}
                                                            onFocus={handleSearchFocus}
                                                            onBlur={handleSearchBlur}
                                                            autoFocus
                                                            id="desktop-search"
                                                            name="desktop-search"
                                                            aria-label="Search products"
                                                            style={{
                                                                minHeight: '48px',
                                                                lineHeight: '48px'
                                                            }}
                                                        />
                                                        <div className="absolute right-0 top-0 h-full flex items-center pr-2">
                                                            <button
                                                                type="button"
                                                                onClick={handleSearchCancel}
                                                                className="p-1 text-gray-400 hover:text-gray-600"
                                                                aria-label="Clear search"
                                                            >
                                                                <XMarkIcon className="h-5 w-5" />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </form>
                                            )}

                                            <div className="flex items-center py-[7px]">
                                                <button
                                                    onClick={toggleSearchMode}
                                                    className="flex items-center justify-center w-12 h-12 text-gray-800 hover:bg-gray-100 rounded-lg dark:text-neutral-200 dark:hover:bg-neutral-700"
                                                    aria-label="Search"
                                                >
                                                    <SearchIcon sx={{ fontSize: 24 }} />
                                                </button>
                                                <a
                                                    className="flex items-center justify-center w-12 h-12 text-gray-800 hover:bg-gray-100 rounded-lg dark:text-neutral-200 dark:hover:bg-neutral-700 ml-2"
                                                    href="/shop/basket/"
                                                    aria-label="Shopping basket"
                                                >
                                                    <div className="relative">
                                                        <LocalMallIcon sx={{ fontSize: 24 }} />
                                                        {basketCount > 0 && (
                                                            <div className={`absolute -top-2 -right-2 bg-blue-800 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold transform ${isUpdating ? 'animate-bounce' : ''}`}>
                                                                {basketCount}
                                                            </div>
                                                        )}
                                                    </div>
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </nav>
            </header>

            {/* Add a spacer div to prevent content from hiding behind the fixed header */}
            <div className="h-[60px] md:h-[144px]"></div>

            {/* Mobile Side Panel - increased z-index */}
            {showMobilePanel && (
                <>
                    {/* Overlay - adjusted z-index to be between header and panel */}
                    <div 
                        className="fixed inset-0 bg-black bg-opacity-50 z-[51]" 
                        onClick={toggleMobilePanel}
                    ></div>
                    {/* Panel - highest z-index */}
                    <div className="fixed inset-y-0 right-0 w-64 bg-white shadow-lg z-[52] overflow-y-auto dark:bg-neutral-800">
                        <div className="flex justify-between items-center p-4 border-b">
                            <Typography variant="h6" className="font-semibold">
                                Menu
                            </Typography>
                            <button
                                onClick={toggleMobilePanel}
                                className="text-gray-500 hover:text-gray-700 dark:text-neutral-400 dark:hover:text-neutral-200"
                            >
                                <CloseIcon />
                            </button>
                        </div>

                        {/* Main Navigation */}
                        <div className="py-2">
                            {!isLoading && menuItems.map((item) => (
                                <div key={item.id}>
                                    <div
                                        onClick={() => handleMenuItemClick(item)}
                                        className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-neutral-200 dark:hover:bg-neutral-700"
                                    >
                                        <span className="text-sm">{item.name}</span>
                                    </div>
                                    {item.item_type === 'dropdown' && activeDropdown === item.id && (
                                        <div className="pl-8 bg-gray-50 dark:bg-neutral-800">
                                            {item.subitems?.map((subitem) => (
                                                <div
                                                    key={subitem.id}
                                                    onClick={() => handleSubItemClick(subitem, item)}
                                                    className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-neutral-300 dark:hover:bg-neutral-700 cursor-pointer"
                                                >
                                                    {subitem.name}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Common Links (Basket & Profile) */}
                        <div className="py-2 border-t">
                            <Link
                                to={isAuthorized ? "/account/profile" : "/login"}
                                className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-neutral-200 dark:hover:bg-neutral-700"
                                onClick={() => {
                                    scrollToTop();
                                    setShowMobilePanel(false);
                                }}
                            >
                                <AccountCircleIcon className="text-gray-500" />
                                <span className="text-sm">{isAuthorized ? 'Profile' : 'Login'}</span>
                            </Link>
                            <Link
                                to="/shop/basket"
                                className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 dark:text-neutral-200 dark:hover:bg-neutral-700"
                                onClick={() => {
                                    scrollToTop();
                                    setShowMobilePanel(false);
                                }}
                            >
                                <LocalMallIcon className="text-gray-500" />
                                <span className="text-sm">Basket</span>
                                {basketCount > 0 && (
                                    <span className="ml-auto bg-blue-800 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs">
                                        {basketCount}
                                    </span>
                                )}
                            </Link>
                        </div>

                        {/* Account Section */}
                        <div className="py-2 border-t">
                            <div className="px-4 py-2">
                                <Typography variant="subtitle2" className="text-gray-500 font-semibold">
                                    My Account
                                </Typography>
                            </div>
                            {isAuthorized
                                ? navigationLinks.account.authenticated.map((item) => (
                                    <HeaderLink
                                        key={item.id}
                                        link={item.url}
                                        title={item.text}
                                        icon={item.icon}
                                        isMobile
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleMobileLinkClick(item.url);
                                        }}
                                    />
                                ))
                                : navigationLinks.account.guest.map((item) => (
                                    <HeaderLink
                                        key={item.id}
                                        link={item.url}
                                        title={item.text}
                                        icon={item.icon}
                                        isMobile
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleMobileLinkClick(item.url);
                                        }}
                                    />
                                ))
                            }
                        </div>

                        {/* Help & Support */}
                        <div className="py-2 border-t">
                            <div className="px-4 py-2">
                                <Typography variant="subtitle2" className="text-gray-500 font-semibold">
                                    Help & Support
                                </Typography>
                            </div>
                            {navigationLinks.help.map((item) => (
                                <HeaderLink
                                    key={item.id}
                                    link={item.url}
                                    title={item.text}
                                    icon={item.icon}
                                    isMobile
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleMobileLinkClick(item.url);
                                    }}
                                />
                            ))}
                        </div>

                        {/* More Links */}
                        <div className="py-2 border-t">
                            <div className="px-4 py-2">
                                <Typography variant="subtitle2" className="text-gray-500 font-semibold">
                                    More
                                </Typography>
                            </div>
                            {navigationLinks.more.map((item) => (
                                <HeaderLink
                                    key={item.id}
                                    link={item.url}
                                    title={item.text}
                                    icon={item.icon}
                                    isMobile
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleMobileLinkClick(item.url);
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </>
            )}
            {/* Mobile Sticky Footer Navigation */}
            <Box 
                className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 py-2 px-4 md:hidden z-50 dark:bg-neutral-800 dark:border-neutral-700"
                sx={{
                    boxShadow: '0px -4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
            >
                <div className="flex justify-around items-center">
                    {/* Profile/Login Link */}
                    <Link
                        to={isAuthorized ? "/account/profile" : "/login"}
                        className={`flex flex-col items-center hover:text-blue-600 dark:hover:text-blue-400 py-1 
                            ${isActivePath('/account') || isActivePath('/login') 
                                ? 'text-blue-600 dark:text-blue-400' 
                                : 'text-gray-600 dark:text-neutral-300'}`}
                        onClick={scrollToTop}
                    >
                        <AccountCircleIcon sx={{ fontSize: 28 }} />
                        <span className="text-xs mt-1">{isAuthorized ? 'Profile' : 'Login'}</span>
                    </Link>

                    {/* Basket Link */}
                    <Link
                        to="/shop/basket"
                        className={`flex flex-col items-center hover:text-blue-600 dark:hover:text-blue-400 py-1 
                            ${isActivePath('/shop/basket') 
                                ? 'text-blue-600 dark:text-blue-400' 
                                : 'text-gray-600 dark:text-neutral-300'}`}
                        onClick={scrollToTop}
                    >
                        <div className="relative">
                            <LocalMallIcon sx={{ fontSize: 28 }} />
                            {basketCount > 0 && (
                                <div className={`
                                    absolute -top-2 -right-2 
                                    bg-blue-800 text-white 
                                    rounded-full w-5 h-5 
                                    flex items-center justify-center 
                                    text-xs font-bold
                                    transform
                                    ${isUpdating ? 'animate-bounce' : ''}
                                `}>
                                    {basketCount}
                                </div>
                            )}
                        </div>
                        <span className="text-xs mt-1">Basket</span>
                    </Link>
                </div>
            </Box>
            {/* Add SearchDropdown component right after the header */}
            <SearchDropdown />
        </>
    );
}
